const DECISION_PRESENTATION = {
  hold: { label: '维持观察', tone: 'steady' },
  adjust: { label: '建议调整', tone: 'attention' },
  progress: { label: '可考虑进阶', tone: 'positive' },
  regress: { label: '建议适当回退', tone: 'attention' },
  reassess: { label: '需要复评', tone: 'attention' },
  pause: { label: '建议暂停', tone: 'risk' },
  refer: { label: '建议进一步确认', tone: 'risk' },
  close: { label: '本阶段已结束', tone: 'steady' },
};

const READINESS_PRESENTATION = {
  qualified: { label: '证据较完整', tone: 'positive' },
  partial: { label: '部分证据', tone: 'attention' },
  insufficient: { label: '证据不足', tone: 'muted' },
  blocked: { label: '判断受限', tone: 'risk' },
};

const CONFIDENCE_LABELS = {
  high: '高可信度',
  medium: '中等可信度',
  low: '低可信度',
};

function finiteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function timeValue(value) {
  const numeric = finiteNumber(value);
  if (numeric !== null) return numeric;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function signedNumber(value, digits = 1) {
  const number = finiteNumber(value);
  if (number === null) return '暂无对比';
  const rounded = number.toFixed(digits);
  return `${number > 0 ? '+' : ''}${rounded}`;
}

function monthChange(current, previous) {
  const currentValue = finiteNumber(current);
  const previousValue = finiteNumber(previous);
  if (currentValue === null || previousValue === null || previousValue <= 0) {
    return { label: '暂无完整上月对比', direction: 'unknown' };
  }
  const percentage = Math.round(
    ((currentValue - previousValue) / previousValue) * 100,
  );
  return {
    label:
      percentage === 0
        ? '与上月持平'
        : `较上月 ${percentage > 0 ? '+' : ''}${percentage}%`,
    direction: percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral',
  };
}

function latestGoalChange(timelineEvidence = []) {
  return timelineEvidence
    .filter((item) => item?.evidenceType === 'member_goal_change')
    .slice()
    .sort(
      (left, right) =>
        timeValue(right?.occurredAt) - timeValue(left?.occurredAt),
    )[0];
}

function preferredBodyMetric(goal, bodyMetricTrends = []) {
  const target = String(goal || '');
  const preferredCodes = /增肌|力量/.test(target)
    ? ['arm', 'chest', 'thigh', 'weight']
    : /减脂|减重|塑形|体型|腰腹/.test(target)
    ? ['waist', 'weight']
    : ['weight', 'waist'];
  return (
    preferredCodes
      .map((code) => bodyMetricTrends.find((item) => item?.metricCode === code))
      .find(Boolean) || bodyMetricTrends[0]
  );
}

function evidenceSources(model) {
  const trainingRecorded =
    Number(model?.monthlyTrainingSummary?.sessions) > 0 ||
    Number(model?.weeklyTrainingSummary?.courseMinutes) > 0 ||
    Number(model?.weeklyTrainingSummary?.sportMinutes) > 0;
  const sources = [
    {
      key: 'training',
      label: '训练与运动事实',
      available: trainingRecorded,
      status: trainingRecorded ? 'available' : 'missing',
      tab: 'archive',
    },
    {
      key: 'capability',
      label: '运动能力',
      available: Number(model?.evaluatedCapabilityCount) > 0,
      status:
        Number(model?.evaluatedCapabilityCount) > 0 ? 'available' : 'missing',
      tab: 'capability',
    },
    {
      key: 'body',
      label: '身体变化',
      available: (model?.bodyMetricTrends || []).length > 0,
      status:
        (model?.bodyMetricTrends || []).length > 0 ? 'available' : 'missing',
      tab: 'archive',
    },
    {
      key: 'communication',
      label: '训练沟通',
      available: (model?.communicationRows || []).length > 0,
      status:
        (model?.communicationRows || []).length > 0
          ? 'available'
          : model?.communicationLoading
          ? 'loading'
          : 'missing',
      tab: 'communication',
    },
  ];
  return sources;
}

function keyIndicators(model) {
  const {
    latestMonth = {},
    previousMonth = {},
    monthlyTrainingSummary = {},
    weeklyTrainingSummary = {},
    evaluatedCapabilityCount = 0,
    capabilityAccounts = [],
    bodyMetricTrends = [],
    profile = {},
  } = model || {};
  const trainingChange = monthChange(
    latestMonth.totalSportMinute,
    previousMonth.totalSportMinute,
  );
  const bodyMetric = preferredBodyMetric(profile.sportTarget, bodyMetricTrends);
  const indicators = [
    {
      key: 'training-minutes',
      label: latestMonth.month
        ? `${latestMonth.month} 训练投入`
        : '最近月训练投入',
      value: finiteNumber(latestMonth.totalSportMinute) || 0,
      unit: '分钟',
      change: trainingChange.label,
      direction: trainingChange.direction,
      source: '完课与运动打卡',
      tab: 'archive',
    },
    {
      key: 'active-days',
      label: '本周运动规律性',
      value: finiteNumber(weeklyTrainingSummary.activeDays) || 0,
      unit: '个活跃日',
      change: `课程 ${
        finiteNumber(weeklyTrainingSummary.courseMinutes) || 0
      } 分钟 · 运动 ${
        finiteNumber(weeklyTrainingSummary.sportMinutes) || 0
      } 分钟`,
      direction:
        Number(weeklyTrainingSummary.activeDays) > 0 ? 'neutral' : 'unknown',
      source: '最近 7 日训练事实',
      tab: 'archive',
    },
    {
      key: 'capability',
      label: '运动能力证据覆盖',
      value: finiteNumber(evaluatedCapabilityCount) || 0,
      unit: '项已评分',
      change: capabilityAccounts.length
        ? `共 ${capabilityAccounts.length} 个能力维度`
        : '暂无能力账户数据',
      direction: Number(evaluatedCapabilityCount) > 0 ? 'neutral' : 'unknown',
      source: '运动能力账户',
      tab: 'capability',
    },
  ];
  if (bodyMetric) {
    indicators.push({
      key: `body-${bodyMetric.metricCode}`,
      label: `${bodyMetric.metricName || '身体指标'}变化`,
      value: finiteNumber(bodyMetric.latestValue) ?? '-',
      unit: bodyMetric.unit || '',
      change: `较首条 ${signedNumber(bodyMetric.change)} ${
        bodyMetric.unit || ''
      }`.trim(),
      direction:
        Number(bodyMetric.change) > 0
          ? 'up'
          : Number(bodyMetric.change) < 0
          ? 'down'
          : 'neutral',
      source: `近 ${bodyMetric.points?.length || 0} 条身体记录`,
      tab: 'archive',
    });
  } else {
    indicators.push({
      key: 'training-sessions',
      label: '近 6 月训练积累',
      value: finiteNumber(monthlyTrainingSummary.sessions) || 0,
      unit: '次记录',
      change: `${finiteNumber(monthlyTrainingSummary.days) || 0} 个训练日`,
      direction:
        Number(monthlyTrainingSummary.sessions) > 0 ? 'neutral' : 'unknown',
      source: '训练事实汇总',
      tab: 'archive',
    });
  }
  return indicators;
}

export function buildGoalInsight(model = {}) {
  const recommendation = model.currentRecommendation;
  const goal = model.profile?.sportTarget || '';
  const problems = recommendation?.mainProblems || [];
  const goalChange = latestGoalChange(model.timelineEvidence);
  const sources = evidenceSources(model);
  const availableSourceCount = sources.filter((item) => item.available).length;
  const decision = recommendation?.decision
    ? DECISION_PRESENTATION[recommendation.decision] || {
        label: '已有训练判断',
        tone: 'steady',
      }
    : goal
    ? { label: '证据积累中', tone: 'muted' }
    : { label: '目标待明确', tone: 'muted' };
  const readiness = recommendation?.evidenceReadiness
    ? READINESS_PRESENTATION[recommendation.evidenceReadiness] || {
        label: '证据状态已记录',
        tone: 'muted',
      }
    : availableSourceCount >= 3
    ? { label: '多源数据已汇聚', tone: 'positive' }
    : availableSourceCount >= 1
    ? { label: '证据持续积累', tone: 'attention' }
    : { label: '暂无有效证据', tone: 'muted' };

  return {
    goal: goal || '尚未记录明确目标',
    goalRecordedAt: goalChange?.occurredAt || model.profile?.questionTime,
    goalTimeLabel: goalChange ? '本次目标生效' : '档案记录时间',
    decision,
    readiness,
    assessment:
      recommendation?.assessmentSummary ||
      (goal
        ? '已经围绕当前目标汇聚训练事实，等待形成稳定判断。'
        : '需要先明确会员当前训练目标，再组织相关证据。'),
    problems,
    primaryProblem: problems[0] || null,
    recommendation:
      recommendation?.recommendationSummary ||
      '当前只展示已记录事实，证据不足时不生成训练方向建议。',
    confidence:
      CONFIDENCE_LABELS[recommendation?.confidence] ||
      (recommendation ? '可信度已记录' : '等待形成判断'),
    evidenceCount: recommendation?.evidenceRefs?.length || 0,
    reviewDueAt: recommendation?.reviewDueAt,
    asOf: recommendation?.asOf || recommendation?.effectiveAt,
    sources,
    availableSourceCount,
    indicators: keyIndicators(model),
  };
}
